import { Link, useParams, Navigate } from "react-router-dom";
import { Facebook, Linkedin, Mail, Link2, Share2, Twitter } from "lucide-react";
import { toast } from "sonner";
import SEO, { breadcrumbSchema, faqSchema, orgSchema } from "../../lib/seo";
import { ARTICLES, articleBySlug } from "../../data/articles";
import { Reveal, Eyebrow, TrialButton, SectionShell, FAQBlock } from "../../components/shared";

const ORIGIN = process.env.REACT_APP_SITE_ORIGIN || "https://guymezger.com";

const ShareBar = ({ article }) => {
  const url = `${ORIGIN}/blog/${article.slug}`;
  const enc = encodeURIComponent;
  const links = [
    { id: "facebook", label: "Share on Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: "x", label: "Share on X", Icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(article.title)}` },
    { id: "linkedin", label: "Share on LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { id: "email", label: "Share by email", Icon: Mail, href: `mailto:?subject=${enc(article.title)}&body=${enc(`Worth a read: ${article.title}\n\n${url}`)}` },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — long-press the URL instead");
    }
  };
  const native = async () => {
    try {
      await navigator.share({ title: article.title, text: article.metaDescription, url });
    } catch (e) { /* user cancelled */ }
  };
  const btn = "p-2.5 border border-black/15 text-[#5A6270] hover:border-[#0A0B0D] hover:text-[#0A0B0D] transition-colors";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="article-share-bar">
      <span className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270] mr-1">Share:</span>
      {links.map(({ id, label, Icon, href }) => (
        <a key={id} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} data-testid={`share-${id}-btn`} className={btn}>
          <Icon size={16} />
        </a>
      ))}
      <button onClick={copy} aria-label="Copy link" title="Copy link" data-testid="share-copy-btn" className={btn}><Link2 size={16} /></button>
      {typeof navigator !== "undefined" && navigator.share && (
        <button onClick={native} aria-label="Share via device" title="Share" data-testid="share-native-btn" className={btn}><Share2 size={16} /></button>
      )}
    </div>
  );
};

const articleSchema = (a) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: a.title,
  description: a.metaDescription,
  image: a.image,
  datePublished: a.date,
  author: { "@type": "Organization", name: "Mezger Martial Arts Coaching Staff", url: `${ORIGIN}/coaches` },
  publisher: { "@id": `${ORIGIN}/#organization` },
  mainEntityOfPage: `${ORIGIN}/blog/${a.slug}`,
});

export default function BlogPost() {
  const { slug } = useParams();
  const a = articleBySlug(slug);
  if (!a) return <Navigate to="/blog" replace />;
  const others = ARTICLES.filter((x) => x.slug !== slug);
  return (
    <>
      <SEO
        title={a.metaTitle}
        description={a.metaDescription}
        image={a.image}
        type="article"
        schemas={[articleSchema(a), orgSchema(), faqSchema(a.faqs), breadcrumbSchema([["Home", "/"], ["Blog", "/blog"], [a.title, `/blog/${a.slug}`]])]}
      />
      <section className="relative bg-[#0A0B0D] text-white pt-24 pb-16 overflow-hidden grain">
        <img src={a.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/70 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>{a.tag} · {a.readTime}</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="article-title">{a.title}</h1>
            <p className="mt-5 text-white/75 text-lg leading-relaxed">{a.intro}</p>
            <div className="mt-6 bg-white/95 backdrop-blur inline-block p-3"><ShareBar article={a} /></div>
          </Reveal>
        </div>
      </section>

      <SectionShell className="bg-white">
        <article className="max-w-3xl mx-auto">
          {a.sections.map((s, i) => (
            <Reveal key={i} className="mb-10">
              <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl">
                <span className="text-neutral-400 mr-3">{String(i + 1).padStart(2, "0")}</span>{s.h2}
              </h2>
              {s.paras.map((p, j) => (
                <p key={j} className="mt-4 text-[#374151] leading-relaxed text-base sm:text-lg">{p}</p>
              ))}
            </Reveal>
          ))}
          <Reveal>
            <div className="bg-[#0A0B0D] text-white p-8 mt-4">
              <h2 className="font-display font-black uppercase text-2xl">The first class answers everything else.</h2>
              <p className="mt-2 text-white/70 text-sm">Free trial at Hidden Gym Richardson or North Dallas. No experience needed, no sparring on day one, gloves and pads provided.</p>
              <TrialButton campaign={`blog-${a.slug}`} page={`blog-${a.slug}`} testId="article-trial-btn" className="mt-5" />
            </div>
          </Reveal>
          <Reveal className="mt-12">
            <Eyebrow>Quick Answers</Eyebrow>
            <div className="mt-3"><FAQBlock faqs={a.faqs} testPrefix={`article-faq-${a.slug}`} /></div>
          </Reveal>
          <Reveal className="mt-10 border-t border-black/10 pt-6">
            <div className="mb-6"><ShareBar article={a} /></div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#5A6270] mb-3">Keep reading</p>
            <ul className="space-y-2 text-sm">
              {a.related.map(([label, path]) => (
                <li key={path}><Link to={path} className="underline font-bold hover:text-neutral-500">{label}</Link></li>
              ))}
            </ul>
          </Reveal>
        </article>
      </SectionShell>

      <SectionShell>
        <Reveal><Eyebrow>More From the Mats</Eyebrow></Reveal>
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          {others.map((o) => (
            <Link key={o.slug} to={`/blog/${o.slug}`} data-testid={`article-related-${o.slug}`} className="group bg-white border border-black/10 hover:border-[#0A0B0D] transition-colors overflow-hidden flex">
              <img src={o.image} alt="" loading="lazy" className="w-32 sm:w-44 object-cover" />
              <div className="p-5">
                <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-400">{o.tag}</span>
                <h3 className="mt-1 font-display font-black uppercase text-lg leading-tight group-hover:text-neutral-500 transition-colors">{o.title}</h3>
                <p className="mt-1 text-xs text-[#5A6270]">{o.readTime}</p>
              </div>
            </Link>
          ))}
        </div>
      </SectionShell>
    </>
  );
}
